import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useCurrentUser } from '../account/useAuth';
import { AuthApiError } from '../account/authApi';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { Button } from '../components/Button/Button';
import { submitMessage, type MessageKind } from './messagesApi';
import './marketing.css';
import './ContactPage.css';

const KIND_OPTIONS: { value: MessageKind; label: string; hint: string }[] = [
  {
    value: 'feature',
    label: 'Feature request',
    hint: 'A new thing you wish BytaApps could do.',
  },
  {
    value: 'bug',
    label: 'Bug report',
    hint: 'Something is broken or behaving weirdly.',
  },
  {
    value: 'contact',
    label: 'General question',
    hint: 'Anything else.',
  },
];

/**
 * Public Contact page. Anyone can submit — signed-in users get
 * attribution automatically. Lands directly in the admin queue
 * (`GET /admin/messages`).
 */
export function ContactPage(): JSX.Element {
  const subjectId = useId();
  const bodyId = useId();
  const nameId = useId();
  const emailId = useId();

  const { data: user } = useCurrentUser();
  const [kind, setKind] = useState<MessageKind>('feature');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      submitMessage({
        kind,
        subject: subject.trim(),
        body: body.trim(),
        sender_name: name.trim() || undefined,
        sender_email: email.trim() || undefined,
      }),
    onSuccess: () => {
      setSent(true);
      setSubject('');
      setBody('');
    },
  });

  const error =
    submit.error instanceof AuthApiError
      ? submit.error.detail || submit.error.message
      : submit.error?.message;

  const canSubmit = !submit.isPending && subject.trim().length > 0 && body.trim().length > 0;

  return (
    <div className="bl-page">
      <AppHeader subBrand="Contact" privacy="on-device" />
      <main className="bl-marketing">
        <header className="bl-marketing__hero">
          <p className="bl-marketing__eyebrow">Contact</p>
          <h1>Drop us a line</h1>
          <p className="bl-marketing__lead">
            Feature ideas, bug reports, complaints, gushing praise — it all lands in the BytaApps
            admin queue. A grown-up at BytaApps reads every message.
          </p>
        </header>

        {sent ? (
          <section className="bl-contact__success" role="status">
            <h2>Got it. 🙌</h2>
            <p>
              Thanks for sending that in. We&rsquo;ll come back to you on the email you provided if
              it needs a reply.
            </p>
            <p>
              <Button onClick={() => setSent(false)}>Send another</Button>
            </p>
          </section>
        ) : (
          <form
            className="bl-contact__form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) submit.mutate();
            }}
          >
            <fieldset className="bl-contact__kind">
              <legend>What are you sending?</legend>
              {KIND_OPTIONS.map((opt) => (
                // eslint-disable-next-line jsx-a11y/label-has-associated-control -- label wraps both radio + nested-span title; lint rule misses the nested text node
                <label key={opt.value} className="bl-contact__kind-row">
                  <input
                    type="radio"
                    name="kind"
                    value={opt.value}
                    checked={kind === opt.value}
                    onChange={() => setKind(opt.value)}
                    disabled={submit.isPending}
                  />
                  <span>
                    <strong>{opt.label}</strong>
                    <small>{opt.hint}</small>
                  </span>
                </label>
              ))}
            </fieldset>

            <label htmlFor={subjectId} className="bl-contact__label">
              Subject
            </label>
            <input
              id={subjectId}
              type="text"
              value={subject}
              maxLength={120}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="One-line summary"
              disabled={submit.isPending}
              required
              className="bl-contact__input"
            />

            <label htmlFor={bodyId} className="bl-contact__label">
              Tell us more
            </label>
            <textarea
              id={bodyId}
              value={body}
              maxLength={10000}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Be specific. Steps to reproduce a bug, an example of the feature, what you tried, what surprised you."
              disabled={submit.isPending}
              required
              className="bl-contact__textarea"
            />

            {!user && (
              <>
                <p className="bl-contact__signed-out">
                  Not signed in. If you want a reply, share your name and email so we can reach you.
                </p>
                <div className="bl-contact__row">
                  <div className="bl-contact__field">
                    <label htmlFor={nameId} className="bl-contact__label">
                      Your name (optional)
                    </label>
                    <input
                      id={nameId}
                      type="text"
                      value={name}
                      maxLength={80}
                      onChange={(e) => setName(e.target.value)}
                      disabled={submit.isPending}
                      className="bl-contact__input"
                    />
                  </div>
                  <div className="bl-contact__field">
                    <label htmlFor={emailId} className="bl-contact__label">
                      Email (optional)
                    </label>
                    <input
                      id={emailId}
                      type="email"
                      value={email}
                      maxLength={320}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={submit.isPending}
                      className="bl-contact__input"
                    />
                  </div>
                </div>
              </>
            )}

            {error && (
              <p className="bl-contact__error" role="alert">
                {error}
              </p>
            )}

            <div className="bl-contact__actions">
              <Button type="submit" disabled={!canSubmit}>
                {submit.isPending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </form>
        )}
      </main>
      <AppFooter />
    </div>
  );
}
